import { getConnection } from "./adbConnection";
import { AdbScrcpyClient, AdbScrcpyOptions2_7 } from "@yume-chan/adb-scrcpy";
import { DefaultServerPath } from "@yume-chan/scrcpy";
import {
  WebCodecsVideoDecoder,
  WebGLVideoFrameRenderer,
} from "@yume-chan/scrcpy-decoder-webcodecs";
// BASE_URL carries its own trailing slash ("/"), unlike CRA's PUBLIC_URL ("").
const SCRCPY_SERVER_URL = `${import.meta.env.BASE_URL}scrcpy-server-v2.7.bin`;

export interface ScreenMirrorSession {
  canvas: HTMLCanvasElement;
  client: AdbScrcpyClient;
  decoder: WebCodecsVideoDecoder;
  /** Whether the device is a VR headset */
  isVR: boolean;
  /** Whether the stream is raw stereo (two eyes side by side, needs 200% width trick) */
  isStereo: boolean;
  /** CSS rotation to apply on canvas (degrees) */
  cssRotateDeg: number;
  stop: () => Promise<void>;
}

/** Per-device scrcpy configuration (crop, codec, display) */
interface DeviceScrcpyConfig {
  /** Crop rect: "width:height:x:y" — crops BEFORE encoding, saving bitrate */
  crop?: string;
  videoCodec: string;
  /** Whether the device is a VR headset */
  isVR?: boolean;
  /** Which Android display to capture (default 0) */
  displayId?: number;
  /** Lock video orientation: -1=unlocked, 0=portrait, 1=landscape, 2=portraitFlipped, 3=landscapeFlipped */
  lockVideoOrientation?: number;
  /** CSS rotation in degrees to apply on the canvas (for VR lens tilt correction, e.g. Quest 3 canted displays) */
  cssRotateDeg?: number;
}

/** Each entry has keywords that match against the USB device name (case-insensitive) */
const DEVICE_CONFIGS: { keywords: string[]; config: DeviceScrcpyConfig }[] = [
  // Meta Quest
  {
    keywords: ["quest 3s", "quest_3s", "panther"],
    config: { crop: "1600:900:68:495", isVR: true, videoCodec: "h265" },
  },
  {
    keywords: ["quest 3", "quest_3", "eureka"],
    config: { isVR: true, videoCodec: "h264", cssRotateDeg: 20 },
  },
  {
    keywords: ["quest 2", "quest_2", "hollywood"],
    config: { isVR: true, videoCodec: "h264" },
  },
  // Pico
  {
    keywords: ["pico neo 3", "pico_neo_3", "a7h10"],
    config: { isVR: true, videoCodec: "h264" },
  },
  {
    keywords: ["pico 4", "a8110", "phoenix_ovs"],
    config: { crop: "1900:1800:130:180", isVR: true, videoCodec: "h264" },
  },
  { keywords: ["pico g3", "a7q10"], config: { isVR: true, videoCodec: "h264" } },
];

function getDeviceConfig(productName: string): DeviceScrcpyConfig {
  const lower = productName.toLowerCase();
  // Match most specific first (quest 3s before quest 3)
  for (const { keywords, config } of DEVICE_CONFIGS) {
    if (keywords.some((kw) => lower.includes(kw))) {
      return config;
    }
  }
  // Mobile / unknown device — no crop, h264
  return { videoCodec: "h264" };
}

// Black frame detection constants (Meta Horizon OS >= v74 bug)
// See: https://github.com/joranmarcy/scrcpy/tree/fix/opengl-discard-blackframes
const SAMPLE_SIZE = 16;
const BLACK_LUMINANCE_THRESHOLD = 25;
const MAX_NON_BLACK_PIXELS = Math.floor(SAMPLE_SIZE * SAMPLE_SIZE * 0.05);

/**
 * Wraps the library's WebGLVideoFrameRenderer with black-frame filtering.
 * WebGL renders via GPU texture upload with NEAREST filtering — no quality loss.
 */
class BlackFrameFilterRenderer {
  private inner: WebGLVideoFrameRenderer;
  private sampleCanvas: OffscreenCanvas;

  private sampleCtx: any;

  get canvas(): HTMLCanvasElement {
    return this.inner.canvas;
  }

  constructor() {
    this.inner = new WebGLVideoFrameRenderer();
    this.sampleCanvas = new OffscreenCanvas(SAMPLE_SIZE, SAMPLE_SIZE);
    this.sampleCtx = this.sampleCanvas.getContext("2d", { willReadFrequently: true });
  }

  setSize(width: number, height: number): void {
    (this.inner as any).setSize(width, height);
  }

  private isFrameBlack(frame: any): boolean {
    this.sampleCtx.drawImage(frame, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    const { data } = this.sampleCtx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    let nonBlack = 0;
    for (let i = 0; i < data.length; i += 8) {
      const lum = 77 * data[i] + 150 * data[i + 1] + 29 * data[i + 2];
      if (lum >= BLACK_LUMINANCE_THRESHOLD * 256) {
        if (++nonBlack > MAX_NON_BLACK_PIXELS) return false;
      }
    }
    return true;
  }

  draw(frame: any): void {
    if (this.isFrameBlack(frame)) {
      return;
    }
    void this.inner.draw(frame);
  }

  dispose() {
    // WebGLVideoFrameRenderer has no dispose
  }
}

function createRenderer() {
  return new BlackFrameFilterRenderer();
}

/**
 * Start a screen mirror session on the connected ADB device.
 * Uses per-device crop parameters (from the Electron app) so the stream
 * shows a single clean eye view without barrel distortion.
 */
export async function startScreenMirror(deviceProductName: string): Promise<ScreenMirrorSession> {
  const adb = getConnection();
  if (!adb) {
    throw new Error("No ADB connection");
  }

  if (!WebCodecsVideoDecoder.isSupported) {
    throw new Error("WebCodecs API is not supported in this browser");
  }

  const deviceConfig = getDeviceConfig(deviceProductName);
  const {
    videoCodec,
    crop,
    displayId = 0,
    lockVideoOrientation = -1,
    cssRotateDeg = 0,
    isVR: configIsVR,
  } = deviceConfig;
  const isVR = configIsVR ?? !!crop;

  const serverResponse = await fetch(SCRCPY_SERVER_URL);
  if (!serverResponse.ok) {
    throw new Error(`Failed to fetch scrcpy server: ${serverResponse.status}`);
  }

  await AdbScrcpyClient.pushServer(
    adb as any,
    serverResponse.body as unknown as ReadableStream<Uint8Array>,
  );
  const options = new AdbScrcpyOptions2_7({
    video: true,
    audio: false,
    control: false,
    displayId,
    maxSize: 1920,
    maxFps: 60,
    videoBitRate: 40_000_000,
    videoCodecOptions: "repeat-previous-frame-after=100000",
    videoCodec,
    ...(crop ? { crop } : {}),
    ...(lockVideoOrientation !== -1 ? { lockVideoOrientation } : {}),
    tunnelForward: false,
    sendFrameMeta: true,
    sendCodecMeta: true,
    sendDeviceMeta: true,
    sendDummyByte: true,
  });

  const client = await AdbScrcpyClient.start(adb as any, DefaultServerPath, options);

  // Drain server output to prevent backpressure
  if (client.output) {
    void (client.output as any)
      .pipeTo(
        new WritableStream({
          write() {
            /* drain */
          },
        }),
      )
      .catch(() => {
        /* stream ended */
      });
  }

  const videoStreamPromise = client.videoStream;
  if (!videoStreamPromise) {
    await client.close();
    throw new Error("Video stream not available");
  }

  const videoStream = await videoStreamPromise;

  const renderer = createRenderer();
  const canvas = renderer.canvas;

  const decoder = new WebCodecsVideoDecoder({
    codec: videoStream.metadata.codec,
    renderer: renderer as any,
  });

  const pipeAbort = new AbortController();
  void videoStream.stream
    .pipeTo(decoder.writable as unknown as WritableStream, {
      signal: pipeAbort.signal,
    })
    .catch((err: any) => {
      if (err?.name === "AbortError" || String(err?.message).includes("abort")) return;
    });

  // Consume audio stream if present to prevent blocking
  if (client.audioStream) {
    client.audioStream
      .then((audio: any) => {
        if (audio?.stream) {
          const reader = audio.stream.getReader();
          const drain = () => {
            reader
              .read()
              .then(({ done }: { done: boolean }) => {
                if (!done) drain();
              })
              .catch(() => {
                /* expected on close */
              });
          };
          drain();
        }
      })
      .catch(() => {
        /* no audio, fine */
      });
  }

  const stop = async () => {
    // 1. Abort the video pipe so no more frames reach the decoder
    pipeAbort.abort();
    // 2. Close scrcpy client — stops the server-side stream
    try {
      await client.close();
    } catch {
      /* ignore */
    }
    // 3. Then dispose decoder (pipe is already aborted, safe now)
    try {
      decoder.dispose();
    } catch {
      /* ignore */
    }
    try {
      renderer.dispose();
    } catch {
      /* ignore */
    }
    // 3. Kill any lingering scrcpy-server process on the device to free the
    // hardware encoder. Without this, restarting accumulates server instances
    // and each new session gets degraded encoding quality.
    try {
      if (adb) {
        const proc = await adb.subprocess.spawn([
          "sh",
          "-c",
          "kill $(ps -o PID -A | grep -v PID | while read p; do " +
            "cat /proc/$p/cmdline 2>/dev/null | tr '\\0' ' ' | " +
            "grep -q scrcpy && echo $p; done) 2>/dev/null || true",
        ]);
        await proc.stdout.getReader().read();
      }
    } catch {
      /* best-effort cleanup */
    }
  };

  // Stereo = VR headset with no crop (raw two-eye stream needing 200% width trick)
  const isStereo = isVR && !crop;
  return { canvas, client, decoder, isVR, isStereo, cssRotateDeg, stop };
}
